import { useState, type ReactNode } from 'react';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Eye, EyeOff, Loader2, Save } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import type { Parametro } from '@/shared/api/tipos';
import { Interruptor } from '@/shared/ui/Interruptor';

export function useAjustes(grupos: string[]) {
  const clienteDeConsultas = useQueryClient();
  const [cambios, setCambios] = useState<Record<string, string>>({});
  const [guardado, setGuardado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const consultas = useQueries({
    queries: grupos.map((g) => ({
      queryKey: ['parametros', g],
      queryFn: () => api.get<Parametro[]>(`/parametros/${g}`),
    })),
  });

  const porClave = new Map<string, Parametro>();
  const grupoDeClave = new Map<string, string>();
  consultas.forEach((c, i) =>
    (c.data ?? []).forEach((p) => {
      porClave.set(p.clave, p);
      grupoDeClave.set(p.clave, grupos[i] ?? p.grupo);
    }),
  );

  const guardar = useMutation({
    mutationFn: async () => {
      for (const grupo of grupos) {
        const delGrupo = Object.keys(cambios)
          .filter((clave) => grupoDeClave.get(clave) === grupo)
          .map((clave) => ({ clave, valor: cambios[clave] ?? '' }));

        if (delGrupo.length > 0) {
          await api.put<Parametro[]>(`/parametros/${grupo}`, { parametros: delGrupo });
        }
      }
    },
    onSuccess: () => {
      setCambios({});
      setGuardado(true);
      setError(null);
      void clienteDeConsultas.invalidateQueries({ queryKey: ['parametros'] });
    },
    onError: (e) =>
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudieron guardar los cambios'),
  });

  return {
    cargando: consultas.some((c) => c.isPending),
    fallo: consultas.find((c) => c.isError)?.error ?? null,
    error,
    guardado,
    hayCambios: Object.keys(cambios).length > 0,
    guardando: guardar.isPending,

    meta: (clave: string) => porClave.get(clave),

    texto: (clave: string) => {
      const editado = cambios[clave];
      if (editado !== undefined) {
        return editado;
      }

      const valor = porClave.get(clave)?.valor;
      return valor == null ? '' : String(valor);
    },

    editado: (clave: string) => cambios[clave],

    booleano: (clave: string) => {
      const editado = cambios[clave];
      return editado !== undefined ? editado === 'true' : porClave.get(clave)?.valor === true;
    },

    editar: (clave: string, valor: string) => {
      setGuardado(false);
      setCambios((previos) => ({ ...previos, [clave]: valor }));
    },

    alternar: (clave: string, activo: boolean) => {
      setGuardado(false);
      setCambios((previos) => ({ ...previos, [clave]: activo ? 'false' : 'true' }));
    },

    guardarTodo: () => guardar.mutate(),
  };
}

export type Ajustes = ReturnType<typeof useAjustes>;

const COLORES = {
  marca: 'bg-marca-100 text-marca-700',
  azul: 'bg-blue-100 text-blue-700',
  indigo: 'bg-indigo-100 text-indigo-700',
  ambar: 'bg-amber-100 text-amber-700',
  violeta: 'bg-violet-100 text-violet-700',
  rojo: 'bg-red-100 text-red-700',
} as const;

export type ColorDeSeccion = keyof typeof COLORES;

export function Seccion({
  icono: Icono,
  color,
  titulo,
  accion,
  children,
}: {
  icono: LucideIcon;
  color: ColorDeSeccion;
  titulo: string;
  accion?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-armazon-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-armazon-100 bg-armazon-50/60 px-6 py-4">
        <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-armazon-800">
          <span className={`grid size-6 shrink-0 place-items-center rounded-md ${COLORES[color]}`}>
            <Icono className="size-3" aria-hidden />
          </span>
          {titulo}
        </h2>
        {accion}
      </header>
      <div className="p-6">{children}</div>
    </section>
  );
}

export function BannerDeActivacion({
  icono: Icono,
  activo,
  titulo,
  descripcion,
  alCambiar,
  color = 'marca',
}: {
  icono: LucideIcon;
  activo: boolean;
  titulo: string;
  descripcion: string;
  alCambiar: () => void;
  color?: 'marca' | 'azul';
}) {
  const encendido =
    color === 'azul' ? 'border-blue-300 bg-blue-50' : 'border-marca-300 bg-marca-50';
  const cuadro = color === 'azul' ? 'bg-blue-100 text-blue-700' : 'bg-marca-100 text-marca-700';
  const texto = color === 'azul' ? 'text-blue-900' : 'text-marca-900';
  const detalle = color === 'azul' ? 'text-blue-700' : 'text-marca-700';

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-4 rounded-xl border-2 p-5 ${
        activo ? encendido : 'border-armazon-200 bg-armazon-50/60'
      }`}
    >
      <div className="flex items-center gap-4">
        <span
          className={`grid size-12 shrink-0 place-items-center rounded-2xl ${
            activo ? cuadro : 'bg-armazon-100 text-armazon-500'
          }`}
        >
          <Icono className="size-6" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className={`text-sm font-bold uppercase ${activo ? texto : 'text-armazon-700'}`}>
            {titulo}
          </p>
          <p className={`mt-0.5 text-xs uppercase ${activo ? detalle : 'text-armazon-600'}`}>
            {descripcion}
          </p>
        </div>
      </div>
      <Interruptor activo={activo} alCambiar={alCambiar} etiqueta={titulo} />
    </div>
  );
}

export function Campo({
  etiqueta,
  icono: Icono,
  ayuda,
  ancho,
  children,
}: {
  etiqueta: string;
  icono?: LucideIcon;
  ayuda?: string;
  ancho?: string;
  children: ReactNode;
}) {
  return (
    <label className={`grid content-start gap-2 ${ancho ?? ''}`}>
      <span className="text-xs font-semibold uppercase tracking-wider text-armazon-600">
        {etiqueta}
      </span>
      <span className="relative block">
        {Icono && (
          <Icono
            className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-armazon-400"
            aria-hidden
          />
        )}
        {children}
      </span>
      {ayuda && (
        <span className="text-[11px] uppercase tracking-[0.03em] text-armazon-600">{ayuda}</span>
      )}
    </label>
  );
}

export function EntradaDeContrasena({
  ajustes,
  clave,
  marcador = '••••••••',
}: {
  ajustes: Ajustes;
  clave: string;
  marcador?: string;
}) {
  const [visible, setVisible] = useState(false);
  const hayGuardada = ajustes.meta(clave)?.tieneValor ?? false;

  return (
    <>
      <input
        type={visible ? 'text' : 'password'}
        autoComplete="new-password"
        value={ajustes.editado(clave) ?? ''}
        onChange={(e) => ajustes.editar(clave, e.target.value)}
        placeholder={hayGuardada ? marcador : 'Sin configurar'}
        className={`${entrada} pl-10 pr-10`}
      />
      <button
        type="button"
        onClick={() => setVisible(!visible)}
        aria-label={visible ? 'Ocultar' : 'Mostrar'}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-armazon-400 transition-colors hover:text-armazon-700"
      >
        {visible ? (
          <EyeOff className="size-4" aria-hidden />
        ) : (
          <Eye className="size-4" aria-hidden />
        )}
      </button>
    </>
  );
}

export function Botonera({
  valor,
  opciones,
  alElegir,
}: {
  valor: string;
  opciones: { valor: string; etiqueta: string }[];
  alElegir: (valor: string) => void;
}) {
  return (
    <span className="grid grid-cols-3 gap-1.5">
      {opciones.map((o) => {
        const elegida = valor === o.valor;
        return (
          <button
            key={o.valor}
            type="button"
            onClick={() => alElegir(o.valor)}
            aria-pressed={elegida}
            className={[
              'rounded-lg border py-2.5 text-xs font-semibold transition-colors',
              elegida
                ? 'border-blue-400 bg-blue-50 text-blue-700'
                : 'border-armazon-200 text-armazon-600 hover:bg-armazon-50',
            ].join(' ')}
          >
            {o.etiqueta}
          </button>
        );
      })}
    </span>
  );
}

export function FilaConInterruptor({
  icono: Icono,
  color,
  titulo,
  descripcion,
  activo,
  alCambiar,
  children,
}: {
  icono: LucideIcon;
  color: ColorDeSeccion;
  titulo: string;
  descripcion: string;
  activo: boolean;
  alCambiar: () => void;
  children?: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-armazon-200 bg-armazon-50/40">
      <div className="flex items-center justify-between gap-4 p-4">
        <div className="flex min-w-0 items-center gap-3">
          <span className={`grid size-9 shrink-0 place-items-center rounded-lg ${COLORES[color]}`}>
            <Icono className="size-4" aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium uppercase text-armazon-800">{titulo}</p>
            <p className="mt-0.5 text-[11px] uppercase text-armazon-600">{descripcion}</p>
          </div>
        </div>
        <Interruptor activo={activo} alCambiar={alCambiar} etiqueta={titulo} />
      </div>
      {activo && children && <div className="px-4 pb-4 sm:pl-16">{children}</div>}
    </div>
  );
}

export function Resumen({
  puntos,
}: {
  puntos: { etiqueta: string; valor: string; listo: boolean }[];
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {puntos.map((p) => (
        <div
          key={p.etiqueta}
          className={[
            'flex items-center gap-3 rounded-xl border p-4',
            p.listo ? 'border-marca-200 bg-marca-50/60' : 'border-amber-200 bg-amber-50/60',
          ].join(' ')}
        >
          <span
            className={[
              'grid size-8 shrink-0 place-items-center rounded-lg',
              p.listo ? 'bg-marca-100 text-marca-700' : 'bg-amber-100 text-amber-700',
            ].join(' ')}
          >
            {p.listo ? (
              <CheckCircle2 className="size-4" aria-hidden />
            ) : (
              <AlertCircle className="size-4" aria-hidden />
            )}
          </span>
          <span className="min-w-0">
            <span className="block text-[11px] font-medium uppercase tracking-wider text-armazon-600">
              {p.etiqueta}
            </span>
            <span
              className={`block truncate text-sm font-semibold uppercase ${
                p.listo ? 'text-armazon-800' : 'text-amber-800'
              }`}
              title={p.valor}
            >
              {p.valor}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

export function BarraDeGuardado({ ajustes }: { ajustes: Ajustes }) {
  if (!ajustes.hayCambios) {
    return null;
  }
  return (
    <div className="sticky bottom-4 z-10">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-armazon-200 bg-white p-4 shadow-lg">
        <p className="text-sm uppercase tracking-wide text-armazon-600">Hay cambios sin guardar</p>
        <button
          type="button"
          onClick={ajustes.guardarTodo}
          disabled={ajustes.guardando}
          title={ajustes.guardando ? 'Guardando…' : 'Guardar cambios'}
          aria-label={ajustes.guardando ? 'Guardando…' : 'Guardar cambios'}
          className="grid size-11 shrink-0 place-items-center rounded-xl bg-marca-600 text-white shadow-sm transition-colors hover:bg-marca-700 disabled:opacity-50"
        >
          {ajustes.guardando ? (
            <Loader2 className="size-5 animate-spin" aria-hidden />
          ) : (
            <Save className="size-5" aria-hidden />
          )}
        </button>
      </div>
    </div>
  );
}

export function AvisosDeAjustes({ ajustes }: { ajustes: Ajustes }) {
  return (
    <>
      {ajustes.error && (
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
        >
          {ajustes.error}
        </p>
      )}
      {ajustes.guardado && !ajustes.hayCambios && (
        <p className="flex items-center gap-2 rounded-lg border border-marca-300 bg-marca-50 px-4 py-3 text-sm text-marca-900">
          <CheckCircle2 className="size-4 shrink-0" aria-hidden />
          <span className="uppercase">Los cambios se guardaron.</span>
        </p>
      )}
    </>
  );
}

export function CargandoAjustes({ bloques = 3 }: { bloques?: number }) {
  return (
    <div className="grid gap-5">
      {Array.from({ length: bloques }, (_, i) => (
        <div key={i} className="h-48 animate-pulse rounded-xl bg-white" />
      ))}
    </div>
  );
}

export function FalloDeAjustes({ causa }: { causa: unknown }) {
  return (
    <p
      role="alert"
      className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
    >
      {causa instanceof ErrorDeApi ? causa.message : 'No se pudieron cargar los parámetros.'}
    </p>
  );
}

export const entrada =
  'h-11 w-full rounded-lg border border-armazon-200 bg-white px-3 text-sm text-armazon-900 outline-none transition-colors placeholder:text-armazon-400 focus:border-marca-400 focus:ring-2 focus:ring-marca-500/20';
